<script setup>
import { reactive, computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useVuelidate } from '@vuelidate/core';
import { required, minLength } from '@vuelidate/validators';
import { useMapGetter, useStore } from 'dashboard/composables/store';

import Input from 'dashboard/components-next/input/Input.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import TagMultiSelectComboBox from 'dashboard/components-next/combobox/TagMultiSelectComboBox.vue';
import WhatsAppOptions from '../../../../NewConversation/components/WhatsAppOptions.vue';
import { validateAudienceCsv } from 'dashboard/helper/campaignCsvHelper';

const emit = defineEmits(['submit', 'cancel']);

const { t } = useI18n();
const store = useStore();

const formState = {
  uiFlags: useMapGetter('campaigns/getUIFlags'),
  labels: useMapGetter('labels/getLabels'),
  inboxes: useMapGetter('inboxes/getWhatsappInboxes'),
};

const initialState = {
  title: '',
  message: '',
  inboxId: null,
  scheduledAt: null,
  selectedAudience: [],
  selectedInbox: null,
  attachment: null,
  audienceCsv: null,
};

const state = reactive({ ...initialState });

const SAMPLE_CSV_URL = '/downloads/campaign-audience-sample.csv';

const csvFileInput = ref(null);
const csvSummary = ref(null);
const csvError = ref('');

// The CSV supplies its own audience, so the label picker is only required without one
const hasCsvAudience = computed(() => !!state.audienceCsv);

const rules = {
  title: { required, minLength: minLength(1) },
  message: { required, minLength: minLength(1) },
  inboxId: { required },
  scheduledAt: { required },
  selectedAudience: {
    required: value => hasCsvAudience.value || value?.length > 0,
  },
};

const v$ = useVuelidate(rules, state);

const isCreating = computed(() => formState.uiFlags.value.isCreating);
let isFetching = ref(false);

const currentDateTime = computed(() => {
  // Added to disable the scheduled at field from being set to the current time
  const now = new Date();
  const localTime = new Date(now.getTime() - now.getTimezoneOffset() * 60000);
  return localTime.toISOString().slice(0, 16);
});

const mapToOptions = (items, valueKey, labelKey) =>
  items?.map(item => ({
    value: item[valueKey],
    label: item[labelKey],
  })) ?? [];

const audienceList = computed(() =>
  mapToOptions(formState.labels.value, 'id', 'title')
);

const inboxOptions = computed(() =>
  mapToOptions(formState.inboxes.value, 'id', 'name')
);

const getErrorMessage = (field, errorKey) => {
  const baseKey = 'CAMPAIGN.WHATSAPP.CREATE.FORM';
  return v$.value[field].$error ? t(`${baseKey}.${errorKey}.ERROR`) : '';
};

const formErrors = computed(() => ({
  title: getErrorMessage('title', 'TITLE'),
  message: getErrorMessage('message', 'MESSAGE'),
  inbox: getErrorMessage('inboxId', 'INBOX'),
  scheduledAt: getErrorMessage('scheduledAt', 'SCHEDULED_AT'),
  audience: getErrorMessage('selectedAudience', 'AUDIENCE'),
}));

const isSubmitDisabled = computed(() => v$.value.$invalid);

const formatToUTCString = localDateTime =>
  localDateTime ? new Date(localDateTime).toISOString() : null;

const clearCsv = () => {
  state.audienceCsv = null;
  csvSummary.value = null;
  csvError.value = '';
  if (csvFileInput.value) csvFileInput.value.value = null;
};

const resetState = () => {
  Object.assign(state, initialState);
  clearCsv();
};

const handleCsvSelect = async event => {
  const file = event.target.files?.[0];
  if (!file) return clearCsv();

  const result = validateAudienceCsv(await file.text());

  if (!result.valid) {
    clearCsv();
    csvError.value = t(
      `CAMPAIGN.WHATSAPP.CREATE.FORM.CSV.ERRORS.${result.error}`,
      {
        count: result.rowsWithoutLabel,
      }
    );
    return undefined;
  }

  state.audienceCsv = file;
  csvSummary.value = { name: file.name, ...result };
  csvError.value = '';
  return undefined;
};

const syncTemplates = async () => {
  if (state.selectedInbox) {
    isFetching.value = true;
    await store.dispatch('inboxes/syncWhatsappTemplates', {
      inboxId: state.selectedInbox.id,
      successMessage: t('CAMPAIGN.WHATSAPP.CREATE.FORM.SYNC_BUTTON.FETCHED'),
      errorMessage: t('CAMPAIGN.WHATSAPP.CREATE.FORM.SYNC_BUTTON.ERROR'),
    });
    isFetching.value = false;
  }
};

const handleCancel = () => emit('cancel');

const prepareCampaignDetails = () => ({
  title: state.title,
  message: state.message,
  inbox_id: state.inboxId,
  scheduled_at: formatToUTCString(state.scheduledAt),
  audience: state.selectedAudience?.map(id => ({
    id,
    type: 'Label',
  })),
  attachment: state.attachment,
  audienceCsv: state.audienceCsv,
});

const handleSubmit = async () => {
  const isFormValid = await v$.value.$validate();
  if (!isFormValid) return;

  emit('submit', prepareCampaignDetails());
  resetState();
  handleCancel();
};
const onInboxChange = id => {
  const inboxList = formState.inboxes.value; // Call the getter function
  state.selectedInbox = inboxList.find(inbox => inbox.id === id) || null;
};
const selectedTemplate = ({ message, templateParams, image }) => {
  var template = {
    message: { content: message, template_params: templateParams },
  };
  state.message = JSON.stringify(template);
  state.attachment = image || null;
};

watch(
  () => [formState.inboxes.value, state.inboxId],
  ([inboxes, id]) => {
    state.selectedInbox = inboxes.find(inbox => inbox.id === id) || null;
  },
  { immediate: true, deep: true }
);
</script>

<template>
  <form class="flex flex-col gap-4" @submit.prevent="handleSubmit">
    <Input
      v-model="state.title"
      :label="t('CAMPAIGN.WHATSAPP.CREATE.FORM.TITLE.LABEL')"
      :placeholder="t('CAMPAIGN.WHATSAPP.CREATE.FORM.TITLE.PLACEHOLDER')"
      :message="formErrors.title"
      :message-type="formErrors.title ? 'error' : 'info'"
    />

    <!-- // add template picker -->

    <div class="flex flex-col gap-1">
      <label for="inbox" class="mb-0.5 text-sm font-medium text-n-slate-12">
        {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.INBOX.LABEL') }}
      </label>
      <ComboBox
        id="inbox"
        v-model="state.inboxId"
        :options="inboxOptions"
        :has-error="!!formErrors.inbox"
        :placeholder="t('CAMPAIGN.WHATSAPP.CREATE.FORM.INBOX.PLACEHOLDER')"
        :message="formErrors.inbox"
        class="[&>div>button]:bg-n-alpha-black2 [&>div>button:not(.focused)]:dark:outline-n-weak [&>div>button:not(.focused)]:hover:!outline-n-slate-6"
        @update:model-value="onInboxChange"
      />
    </div>

    <div class="flex flex-col gap-1 z-10">
      <label for="templates" class="mb-0.5 text-sm font-medium text-n-slate-12">
        {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.TEMPLATES.LABEL') }}
      </label>
      <WhatsAppOptions
        v-if="state.selectedInbox"
        :inbox-id="state.inboxId"
        :is-fetching="isFetching"
        parent="Campaign"
        @send-message="selectedTemplate"
        @sync-templates="syncTemplates"
      />
    </div>

    <div class="flex flex-col gap-1">
      <label for="audience" class="mb-0.5 text-sm font-medium text-n-slate-12">
        {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.AUDIENCE.LABEL') }}
      </label>
      <TagMultiSelectComboBox
        v-if="!hasCsvAudience"
        v-model="state.selectedAudience"
        :options="audienceList"
        :label="t('CAMPAIGN.WHATSAPP.CREATE.FORM.AUDIENCE.LABEL')"
        :placeholder="t('CAMPAIGN.WHATSAPP.CREATE.FORM.AUDIENCE.PLACEHOLDER')"
        :has-error="!!formErrors.audience"
        :message="formErrors.audience"
        class="[&>div>button]:bg-n-alpha-black2"
      />

      <div class="flex flex-wrap items-center gap-2">
        <Button
          type="button"
          sm
          faded
          slate
          icon="i-lucide-upload"
          :label="t('CAMPAIGN.WHATSAPP.CREATE.FORM.CSV.UPLOAD')"
          @click="csvFileInput?.click()"
        />
        <span v-if="csvSummary" class="text-sm text-n-slate-11">
          {{
            t('CAMPAIGN.WHATSAPP.CREATE.FORM.CSV.SELECTED', {
              name: csvSummary.name,
              count: csvSummary.rowCount,
            })
          }}
        </span>
        <Button
          v-if="csvSummary"
          type="button"
          sm
          ghost
          slate
          :label="t('CAMPAIGN.WHATSAPP.CREATE.FORM.CSV.REMOVE')"
          @click="clearCsv"
        />
      </div>

      <p v-if="csvError" class="text-sm text-n-ruby-9">{{ csvError }}</p>
      <p class="text-xs text-n-slate-11">
        {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.CSV.HELP') }}
        <a
          :href="SAMPLE_CSV_URL"
          target="_blank"
          rel="noopener noreferrer"
          download="campaign-audience-sample.csv"
          class="text-n-blue-11"
        >
          {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.CSV.DOWNLOAD_SAMPLE') }}
        </a>
      </p>

      <input
        ref="csvFileInput"
        type="file"
        accept=".csv,text/csv"
        class="hidden"
        @change="handleCsvSelect"
      />
    </div>

    <Input
      v-model="state.scheduledAt"
      :label="t('CAMPAIGN.WHATSAPP.CREATE.FORM.SCHEDULED_AT.LABEL')"
      type="datetime-local"
      :min="currentDateTime"
      :placeholder="t('CAMPAIGN.WHATSAPP.CREATE.FORM.SCHEDULED_AT.PLACEHOLDER')"
      :message="formErrors.scheduledAt"
      :message-type="formErrors.scheduledAt ? 'error' : 'info'"
    />

    <div class="flex items-center justify-between w-full gap-3">
      <Button
        variant="faded"
        color="slate"
        type="button"
        :label="t('CAMPAIGN.WHATSAPP.CREATE.FORM.BUTTONS.CANCEL')"
        class="w-full bg-n-alpha-2 n-blue-text hover:bg-n-alpha-3"
        @click="handleCancel"
      />
      <Button
        :label="t('CAMPAIGN.WHATSAPP.CREATE.FORM.BUTTONS.CREATE')"
        class="w-full"
        type="submit"
        :is-loading="isCreating"
        :disabled="isCreating || isSubmitDisabled"
      />
    </div>
  </form>
</template>
